import { ComponentType, ReactNode, useCallback } from 'react';
import { useToggleBoolean } from '@bridebook/toolbox/src/utils/hooks/use-toggle-boolean';
import {
  Box,
  Collapse,
  Divider,
  IIconProps,
  IconRoundButton,
  IncludedInPricing,
  TColor,
} from '@bridebook/ui';
import componentStyles from '@bridebook/ui/src/components/pricing-accordion/pricing-accordion.style';
import { EPricingDealType } from '@bridebook/ui/src/components/pricing-accordion/types';
import {
  IconCatering,
  IconChevron,
  IconGuestSingle,
  IconPackage,
  IconProps,
  IconVenue2,
} from '@bridebook/ui/src/icons/dynamic';

const PRICING_DEALS_ICONS: Record<
  EPricingDealType,
  {
    IconComponent: ComponentType<IconProps | IIconProps>;
    backgroundColor: TColor;
    iconColor: TColor;
    extraIconProps?: IconProps;
  }
> = {
  [EPricingDealType.CATERING]: {
    IconComponent: IconCatering,
    backgroundColor: 'indigoCrush20',
    iconColor: 'space',
  },
  [EPricingDealType.PACKAGES_ALL]: {
    IconComponent: IconPackage,
    backgroundColor: 'indigoCrush20',
    iconColor: 'space',
  },
  [EPricingDealType.PACKAGES_PP]: {
    IconComponent: IconGuestSingle,
    backgroundColor: 'indigoCrush20',
    iconColor: 'space',
  },
  [EPricingDealType.VENUE_HIRE]: {
    IconComponent: IconVenue2,
    backgroundColor: 'teal40',
    iconColor: 'space',
    extraIconProps: { pb: 1 },
  },
};

interface IProps {
  dealType: EPricingDealType;
  label?: string;
  priceLabel?: string;
  initiallyExpanded?: boolean;
  guests?: number;
  icon?: ReactNode;
  includedInPricing: string[];
  info?: string;
  labelInfo?: string;
  guestsInfo?: ReactNode;
  isMobile: boolean;
  onShowDetailsClick?: (label: string) => void;
  texts: {
    includedInPricingTitle: string;
    showDetailsCta: string;
    hideDetailsCta: string;
  };
}

const PricingAccordion = ({
  dealType,
  priceLabel,
  initiallyExpanded,
  icon,
  includedInPricing,
  info,
  label = '',
  guestsInfo,
  isMobile,
  onShowDetailsClick,
  texts,
}: IProps) => {
  const [expanded, , , toggleExpanded] = useToggleBoolean(initiallyExpanded);

  const isExpandable =
    (includedInPricing && Object.entries(includedInPricing).length > 0) || !!info;

  const styles = componentStyles({ expanded, isExpandable });

  const handleOnClick = useCallback(() => {
    if (!isExpandable) return;

    if (!expanded) {
      onShowDetailsClick?.(label);
    }

    toggleExpanded();
  }, [isExpandable, expanded, toggleExpanded, onShowDetailsClick, label]);

  const { IconComponent, ...iconProps } = PRICING_DEALS_ICONS[dealType];
  const iconCircleSize = isMobile ? 40 : 44;
  const iconSize = 22;

  return (
    <Box style={styles.root}>
      <Box style={styles.headingWrapper} onClick={handleOnClick}>
        {icon ? (
          <Box style={styles.icon}>{icon}</Box>
        ) : (
          <IconRoundButton
            size={iconCircleSize}
            backgroundColor={iconProps.backgroundColor}
            icon={
              <IconComponent
                width={iconSize}
                color={iconProps.iconColor}
                {...iconProps.extraIconProps}
              />
            }
          />
        )}
        <Box style={styles.heading}>
          {label && <Box style={styles.label}>{label}</Box>}
          {priceLabel && <Box style={styles.priceLabel}>{priceLabel}</Box>}
          {guestsInfo && <Box style={styles.guestsInfo}>{guestsInfo}</Box>}
          {isExpandable && (
            <Box as="a" style={styles.showDetailsLink} className="show-details-link">
              {texts.showDetailsCta}
            </Box>
          )}
        </Box>
        {isExpandable && (
          <Box style={styles.chevron}>
            <IconChevron color="space75" width={isMobile ? 16 : 14} />
          </Box>
        )}
      </Box>
      {isExpandable && (
        <Collapse isOpen={expanded}>
          <Box style={styles.expandedContentWrapper}>
            {includedInPricing && Object.entries(includedInPricing).length > 0 && (
              <Box style={styles.included}>
                <Divider spacingBottom={0} />
                <IncludedInPricing
                  items={includedInPricing}
                  includedInPricingTitle={texts.includedInPricingTitle}
                />
              </Box>
            )}
            {info && (
              <>
                <Divider spacingBottom={4} />
                <Box style={styles.info}>{info}</Box>
              </>
            )}
            <Box as="a" style={styles.hideDetailsLink} onClick={handleOnClick}>
              {texts.hideDetailsCta}
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default PricingAccordion;
